/* Admin API client.
 *
 * Two distinct backends:
 *  1. SnappyMail admin JSON API — base `?/admin/Json/&q[]=/0/` (the /admin path
 *     is what separates admin from user sessions; the admin session cookie
 *     `smadmin` is set by AdminLogin). Separate CSRF token from the user client.
 *  2. Account bridge — `/admin-api/...` (same-origin in dev via the Vite proxy,
 *     which injects the Authorization header server-side; the frontend never
 *     sees the bridge token).
 */

const ADMIN_JSON_BASE = "/?/admin/Json/&q[]=/0/";
let adminToken = "";

const ERROR_MESSAGES: Record<number, string> = {
    101: "Session expired — reload the page and try again.",
    102: "Invalid credentials.",
    903: "Invalid request.",
};

class AdminApiError extends Error {
    code: number;
    constructor(code: number, raw: string) {
        // SnappyMail returns notification codes like "AuthError[102]"; map the
        // known ones to human-readable text instead of leaking internals.
        const message = ERROR_MESSAGES[code] ?? (raw || "Request failed");
        super(message);
        this.code = code;
    }
}

export async function adminBoot(): Promise<{ Auth: boolean; System?: { token: string } }> {
    const res = await fetch(`/?/AdminAppData/0/${Math.random().toString().slice(2)}/`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
    });
    const data = (await res.json()) as { Auth: boolean; System?: { token: string } };
    adminToken = data.System?.token ?? "";
    return data;
}

async function adminPost<T>(action: string, params: object = {}): Promise<T> {
    const res = await fetch(ADMIN_JSON_BASE, {
        method: "POST",
        credentials: "same-origin",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-SM-Token": adminToken,
        },
        body: JSON.stringify({ Action: action, ...params }),
    });
    const data = (await res.json()) as { Result: T | false; code?: number; message?: string };
    if (data.Result === false || data.code) {
        throw new AdminApiError(data.code ?? 0, data.message ?? `Admin action ${action} failed`);
    }
    const result = data.Result as T & { System?: { token: string } };
    if (result?.System?.token) adminToken = result.System.token;
    return result;
}

/* ---- SnappyMail admin API ---- */

export async function adminLogin(login: string, password: string): Promise<{ Auth: boolean }> {
    return adminPost<{ Auth: boolean }>("AdminLogin", { Login: login, Password: password });
}

export async function adminLogout(): Promise<void> {
    await adminPost<unknown>("AdminLogout");
}

export interface AdminSettingsResult {
    webmail?: Record<string, unknown>;
    interface?: Record<string, unknown>;
    security?: Record<string, unknown>;
    login?: Record<string, unknown>;
    [section: string]: unknown;
}

export async function adminSettingsGet(): Promise<AdminSettingsResult> {
    return adminPost<AdminSettingsResult>("AdminSettingsGet");
}

export async function adminPasswordUpdate(login: string, current: string, next: string): Promise<{ Weak?: boolean }> {
    return adminPost<{ Weak?: boolean }>("AdminPasswordUpdate", { Login: login, Password: current, newPassword: next });
}

export async function adminClearCache(): Promise<void> {
    await adminPost<unknown>("AdminClearCache");
}

/* ---- account bridge (docker-mailserver) ---- */

export interface BridgeAccount {
    email: string;
    mailboxSizeMb: number;
}

export interface BridgeAlias {
    source: string;
    destination: string;
}

export interface BridgeQuota {
    email: string;
    limit: number | null;
    unit: string | null;
}

async function bridge<T>(path: string, init: RequestInit = {}): Promise<T> {
    const res = await fetch(`/admin-api${path}`, {
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
        ...init,
    });
    const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    if (!res.ok) throw new AdminApiError(res.status, (data as { error?: string }).error ?? `HTTP ${res.status}`);
    return data as T;
}

export function bridgeAccounts(): Promise<{ accounts: BridgeAccount[] }> {
    return bridge("/api/accounts");
}

export function bridgeCreateAccount(email: string, password: string): Promise<{ ok: boolean }> {
    return bridge("/api/accounts", { method: "POST", body: JSON.stringify({ email, password }) });
}

export function bridgeDeleteAccount(email: string): Promise<{ ok: boolean }> {
    return bridge(`/api/accounts/${encodeURIComponent(email)}`, { method: "DELETE" });
}

export function bridgeSetPassword(email: string, password: string): Promise<{ ok: boolean }> {
    return bridge(`/api/accounts/${encodeURIComponent(email)}/password`, {
        method: "PUT",
        body: JSON.stringify({ password }),
    });
}

export function bridgeAliases(): Promise<{ aliases: BridgeAlias[] }> {
    return bridge("/api/aliases");
}

export function bridgeCreateAlias(source: string, destination: string): Promise<{ ok: boolean }> {
    return bridge("/api/aliases", { method: "POST", body: JSON.stringify({ source, destination }) });
}

export function bridgeDeleteAlias(source: string, destination: string): Promise<{ ok: boolean }> {
    return bridge(`/api/aliases?source=${encodeURIComponent(source)}&destination=${encodeURIComponent(destination)}`, {
        method: "DELETE",
    });
}

export function bridgeQuotas(): Promise<{ quotas: BridgeQuota[] }> {
    return bridge("/api/quotas");
}

export function bridgeSetQuota(email: string, limit: number): Promise<{ ok: boolean }> {
    return bridge(`/api/quotas/${encodeURIComponent(email)}`, { method: "PUT", body: JSON.stringify({ limit }) });
}

export function bridgeDeleteQuota(email: string): Promise<{ ok: boolean }> {
    return bridge(`/api/quotas/${encodeURIComponent(email)}`, { method: "DELETE" });
}
